import type { ThemeConfig } from 'antd';
import { COLOR_KEYS, CSS_VARIABLES, EMERON, type Theme } from './palette';

/**
 * Puts a theme on the page, and derives Ant Design's own configuration from the same data.
 *
 * The stylesheet's `:root` block carries the default so the first paint is right with no flash; this writes over those
 * custom properties when a different theme is active. Nothing is rebuilt and no stylesheet is swapped — the whole
 * interface is drawn from the variables, so changing them changes everything at once.
 */
export function applyTheme(theme: Theme, root: HTMLElement = document.documentElement): void {
  for (const key of COLOR_KEYS) {
    root.style.setProperty(CSS_VARIABLES[key], theme.colors[key]);
  }
  root.style.setProperty('--radius', `${String(theme.radius)}px`);
  root.style.setProperty('--font', theme.fontFamily);
  root.style.setProperty('--font-ar', theme.fontFamilyAr);
  root.style.setProperty('--font-size', `${String(theme.fontSize)}px`);
  // components that need to know the surface is dark ask this rather than inferring it from a colour
  root.dataset.dark = theme.dark ? 'true' : 'false';
}

/** Undoes applyTheme, so the page falls back to the stylesheet's own `:root` defaults. */
export function clearTheme(root: HTMLElement = document.documentElement): void {
  for (const key of COLOR_KEYS) {
    root.style.removeProperty(CSS_VARIABLES[key]);
  }
  for (const prop of ['--radius', '--font', '--font-ar', '--font-size']) {
    root.style.removeProperty(prop);
  }
  delete root.dataset.dark;
}

/**
 * Ant Design's configuration, derived rather than declared. It used to be a second copy of the palette in hex, which
 * meant every theme change had to be made twice and one of the two was always forgotten.
 */
export function antdThemeFor(theme: Theme = EMERON): ThemeConfig {
  const c = theme.colors;
  return {
    token: {
      colorPrimary: c.gold,
      colorInfo: c.info,
      colorSuccess: c.svc,
      colorWarning: c.quar,
      colorError: c.us,
      colorBgLayout: c.bg,
      colorBgContainer: c.paper,
      // A read-only field is not a disabled control the person failed to reach — on these screens it is a value the
      // document already carries (the supplier a sent order went to, the date it was acknowledged). Ant Design fills
      // those with its own grey, which on a purchase order — nearly all read-only once sent — turned the form into a
      // wall of grey boxes. They take the same ground as the page and say what they are through muted text and a
      // lighter rule instead, so the fields still open for editing are the ones that look open.
      colorBgContainerDisabled: c.paper,
      colorTextDisabled: c.muted,
      colorText: c.ink,
      colorTextSecondary: c.muted,
      colorBorder: c.line,
      colorSplit: c.line2,
      // controls wear the radius, cards a step more, badges a step less (EmeronCore r-8 / r-12 / r-6)
      borderRadius: theme.radius,
      borderRadiusLG: theme.radius + 4,
      borderRadiusSM: Math.max(0, theme.radius - 2),
      fontSize: theme.fontSize,
      controlHeight: 36,
      fontFamily: theme.fontFamily,
      lineHeight: 1.5,
      // the platform's shadows are faint: enough to lift a popover, never a drop shadow on a card
      boxShadow: '0 4px 6px rgba(0,0,0,0.06), 0 2px 4px rgba(0,0,0,0.04)',
      boxShadowSecondary: '0 10px 15px rgba(0,0,0,0.06), 0 4px 6px rgba(0,0,0,0.04)',
    },
    components: {
      Button: { primaryColor: c.onGold, fontWeight: 500, defaultBorderColor: c.fieldBorder, defaultColor: c.gold },
      Table: {
        headerBg: c.rowHead,
        headerColor: c.muted,
        cellPaddingBlock: 7,
        cellPaddingInline: 10,
        rowHoverBg: c.rowHover,
        borderColor: c.line2,
        fontSize: theme.fontSize,
      },
      Tabs: { inkBarColor: c.gold, itemSelectedColor: c.gold, itemColor: c.muted, titleFontSize: theme.fontSize },
      Menu: {
        darkItemBg: c.navy,
        darkItemColor: c.sbHeading,
        darkItemSelectedBg: c.sbHover,
        darkItemSelectedColor: c.sbTextStrong,
        darkSubMenuItemBg: c.navy,
      },
      Modal: { borderRadiusLG: theme.radius + 4, headerBg: c.paper, contentBg: c.paper },
      Input: { activeBorderColor: c.gold, hoverBorderColor: c.placeholder, activeShadow: `0 0 0 3px ${c.gold}33` },
      Select: { optionSelectedBg: c.goldBg },
      Tag: { borderRadiusSM: Math.max(0, theme.radius - 2) },
    },
  };
}
